import React, { useEffect, useMemo, useState } from 'react';
import { isToday, isYesterday, subDays } from 'date-fns';
import { useUserDateFormatter } from '../hooks';
import ActivityForm from './ActivityForm';
import { ActivityFormData, ActivityMessage, ActivityPanelProps } from './types';
import { ActivityIcon } from '../../icons/ActivityIcon';
import { CircularProgress } from '@material-ui/core';
import {
  ActivityPanelContainer,
  ActivityFeed,
  ActivityGroup,
  ActivityGroupHeader,
  ActivityItem as ActivityListItem,
  ActivityContent,
  ActivityTimestamp,
  ActivityDescription,
} from './ActivityPanelStyles';
import { SectionDivider } from '../UserRequestsStyles';
import { FlexBox } from '../../global/FlexBox';
import { normalizeText } from '../../../shared/utils';
import { REQUEST_TYPE } from '../constants';
import { ACTIVITY_TYPE } from './constants';

function mergeActivities(
  current: ActivityMessage[],
  incoming: ActivityMessage[],
): ActivityMessage[] {
  const incomingMessageDescriptions = new Set(
    incoming
      .filter(
        activity =>
          activity.type === ACTIVITY_TYPE.MESSAGE && activity.description,
      )
      .map(activity => normalizeText(activity.description)),
  );

  const optimisticItems = current.filter(
    activity =>
      activity.id.startsWith('optimistic-') &&
      activity.type === ACTIVITY_TYPE.MESSAGE &&
      activity.description &&
      !incomingMessageDescriptions.has(normalizeText(activity.description)),
  );

  return [...optimisticItems, ...incoming];
}

function hasAdditionalRecipients(data: ActivityFormData): boolean {
  return !!data.ccRecipients && data.ccRecipients.length > 0;
}

const ActivityPanel: React.FC<ActivityPanelProps> = ({
  defaultEmail = '',
  activities = [],
  onSendMessage,
  readOnly = false,
  isLoading = false,
  enableCcRecipients = false,
  requestType,
}) => {
  const { formatDate, formatTime } = useUserDateFormatter();
  const [activitiesList, setActivitiesList] =
    useState<ActivityMessage[]>(activities);

  useEffect(() => {
    setActivitiesList(current => mergeActivities(current, activities));
  }, [activities]);

  const groupedActivities = useMemo(() => {
    const groups = {
      today: activitiesList.filter(activity =>
        isToday(new Date(activity.createdDate)),
      ),
      yesterday: activitiesList.filter(activity =>
        isYesterday(new Date(activity.createdDate)),
      ),
      older: activitiesList.filter(activity => {
        const createdDate = new Date(activity.createdDate);
        return !isToday(createdDate) && !isYesterday(createdDate);
      }),
    };

    const sortByTimestamp = (a: ActivityMessage, b: ActivityMessage) =>
      new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();

    return {
      today: groups.today.sort(sortByTimestamp),
      yesterday: groups.yesterday.sort(sortByTimestamp),
      older: groups.older.sort(sortByTimestamp),
    };
  }, [activitiesList]);

  const isMessageType = (type: string) => type === ACTIVITY_TYPE.MESSAGE;

  const getActivityContent = (activity: ActivityMessage) => {
    if (requestType === REQUEST_TYPE.INTAKE) {
      if (activity.type === ACTIVITY_TYPE.REQUEST_CREATED)
        return 'Intake Request Created';
    }
    return activity.description || '-';
  };

  const getActivityTimestamp = (activity: ActivityMessage) => {
    const date = formatDate(activity.createdDate);
    const time = formatTime(activity.createdDate);
    if (isMessageType(activity.type)) {
      return `Support team sent an email on ${date} at ${time}`;
    }
    return `${date} at ${time}`;
  };

  const handleSendMessage = async (data: ActivityFormData) => {
    if (!hasAdditionalRecipients(data)) {
      const now = new Date().toISOString();
      const optimisticMessage: ActivityMessage = {
        id: `optimistic-${Date.now()}`,
        createdDate: now,
        lastModifiedDate: now,
        createdBy: data.email,
        lastModifiedBy: data.email,
        supportRequestId: '',
        type: ACTIVITY_TYPE.MESSAGE,
        description: data.message,
      };

      setActivitiesList(prev => [optimisticMessage, ...prev]);
    }

    if (onSendMessage) {
      await onSendMessage(data);
    }
  };

  const renderActivityGroup = (
    groupKey: string,
    activityItems: ActivityMessage[],
    title: string,
  ) => {
    if (activityItems.length === 0) return null;

    return (
      <ActivityGroup key={groupKey}>
        <ActivityGroupHeader>{title}</ActivityGroupHeader>
        {activityItems.map(activity => (
          <ActivityListItem key={activity.id}>
            <ActivityIcon type={activity.type} />
            <ActivityContent>
              <ActivityTimestamp>
                {getActivityTimestamp(activity)}
              </ActivityTimestamp>
              <ActivityDescription>
                {getActivityContent(activity)}
              </ActivityDescription>
            </ActivityContent>
          </ActivityListItem>
        ))}
      </ActivityGroup>
    );
  };

  return (
    <ActivityPanelContainer>
      {!readOnly && (
        <ActivityForm
          defaultEmail={defaultEmail}
          onSubmit={handleSendMessage}
          isLoading={isLoading}
          enableCcRecipients={enableCcRecipients}
        />
      )}
      <ActivityFeed>
        {isLoading ? (
          <FlexBox
            sx={{
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </FlexBox>
        ) : (
          <>
            <SectionDivider />
            {renderActivityGroup(
              'today',
              groupedActivities.today,
              `Today, ${formatDate(new Date().toISOString())}`,
            )}
            {renderActivityGroup(
              'yesterday',
              groupedActivities.yesterday,
              `Yesterday, ${formatDate(subDays(new Date(), 1).toISOString())}`,
            )}
            {renderActivityGroup('older', groupedActivities.older, 'Older')}
          </>
        )}
      </ActivityFeed>
    </ActivityPanelContainer>
  );
};

export default ActivityPanel;
